import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { Users, Lock } from "lucide-react";
import UserPicker from "./UserPicker";
import ExternalDomainWarning from "./ExternalDomainWarning";
import ColorPicker from "./ColorPicker";
import IconPicker from "./IconPicker";
import { DEFAULT_ICON } from "./icon-registry";
import { useModalDismiss } from "./useModalDismiss";

interface Category {
    id: string;
    name: string;
    description: string | null;
    color: string | null;
    icon: string | null;
    sortMode: string;
    accessLevel: string;
    ownerUserIds?: string[];
    allowedUserIds?: string[];
}

interface Props {
    category: Category | null;
    currentUserId: string | null;
    onSave: () => void;
    onCancel: () => void;
}

const DEFAULT_COLOR = "#3B82F6";

export default function CategoryForm({ category, currentUserId, onSave, onCancel }: Props) {
    const isEdit = !!category;
    const [name, setName] = useState(category?.name || "");
    const [description, setDescription] = useState(category?.description || "");
    const [color, setColor] = useState(category?.color || DEFAULT_COLOR);
    const [icon, setIcon] = useState<string | null>(category?.icon || DEFAULT_ICON);
    const [sortMode, setSortMode] = useState(category?.sortMode || "last_modified");
    const [accessLevel, setAccessLevel] = useState(category?.accessLevel || "public");
    const [owners, setOwners] = useState<string[]>(
        category?.ownerUserIds ?? (currentUserId ? [currentUserId] : [])
    );
    const [allowed, setAllowed] = useState<string[]>(category?.allowedUserIds || []);
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    // on-edit: backend might not include ownerUserIds/allowedUserIds in list DTO — refetch via a single-category GET
    // intentional mount-only effect: modal is re-keyed per category so props are stable for the component's lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isEdit || !category) return;
        if (category.ownerUserIds !== undefined) return;
        axios.get(`/api/manuals/categories/${category.id}`)
            .then(res => {
                if (res.data.success) {
                    setOwners(res.data.item.ownerUserIds || []);
                    setAllowed(res.data.item.allowedUserIds || []);
                }
            }).catch(() => {});
    }, []);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Name is required");
            return;
        }
        setSaving(true);

        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            color,
            icon,
            sortMode,
            accessLevel,
            ownerUserIds: owners,
            allowedUserIds: accessLevel === "restricted" ? allowed : [],
        };

        const request = isEdit
            ? axios.put(`/api/manuals/categories/${category!.id}`, payload)
            : axios.post("/api/manuals/categories", payload);

        request.then(res => {
            if (res.data.success) {
                toast.success(isEdit ? "Category updated" : "Category created");
                dismiss("save");
            } else {
                toast.error(res.data.message || "Failed to save");
                setSaving(false);
            }
        }).catch(err => {
            toast.error(err.response?.data?.message || "Failed to save category");
            setSaving(false);
        });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered scrollable backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>
                        {isEdit ? "Edit category" : "Create new category"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="m-label">Name</label>
                        <input type="text" className="m-input" value={name} onChange={e => setName(e.target.value)}
                            placeholder="e.g. Announcements, Sales Playbook, etc." maxLength={256} autoFocus />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Description (optional)</label>
                        <textarea className="m-input" value={description} onChange={e => setDescription(e.target.value)}
                            placeholder="Type your text..." rows={2} />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Category owners</label>
                        <UserPicker value={owners} onChange={setOwners} placeholder="Add owner..." excludeReadonly />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Color</label>
                        <div><ColorPicker value={color} iconName={icon} onChange={setColor} /></div>
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Icon</label>
                        <IconPicker value={icon} color={color} onChange={setIcon} />
                    </div>

                    <div className="mb-3">
                        <label className="m-label">Access level</label>
                        <div className="m-radio-group">
                            <label className="m-radio">
                                <input type="radio" name="accessLevel" value="public" checked={accessLevel === "public"} onChange={() => setAccessLevel("public")} />
                                <Users size={14} style={{ marginRight: 4 }} /> <span>Everyone at National HealthClaim</span>
                            </label>
                            <label className="m-radio">
                                <input type="radio" name="accessLevel" value="restricted" checked={accessLevel === "restricted"} onChange={() => setAccessLevel("restricted")} />
                                <Lock size={14} style={{ marginRight: 4 }} /> <span>Only invited people</span>
                            </label>
                        </div>
                    </div>

                    {accessLevel === "restricted" && (
                        <div className="mb-3">
                            <label className="m-label">Allowed people</label>
                            <UserPicker value={allowed} onChange={setAllowed} placeholder="Add user..." excludeIds={owners} />
                            <ExternalDomainWarning userIds={allowed} />
                            <div className="m-hint">Owners also have access automatically.</div>
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="m-label">What order should the items appear in?</label>
                        <select className="m-input" value={sortMode} onChange={e => setSortMode(e.target.value)}>
                            <option value="last_modified">last modified</option>
                            <option value="alphabetical">alphabetical</option>
                        </select>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Saving..." : isEdit ? "Update category" : "Create category"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
